import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  useMediaQuery,
  Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchApi } from '../services/api';

interface SearchData {
  도시번호: number;
  도시한글명: string;
  국가번호: number;
  국가한글명: string;
  코드명: string;
}

interface ApiResponse {
  table: SearchData[];
}

interface Country {
  name: string;
  code: number;
}

interface City {
  name: string;
  code: string;
}

interface CountryCitySearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (country?: string, city?: string) => void;
  width: string;
  anchorEl?: HTMLElement | null;
}

const CountryCitySearchDialog: React.FC<CountryCitySearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  width,
  anchorEl,
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [searchData, setSearchData] = useState<SearchData[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [regions, setRegions] = useState<string[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCities, setSelectedCities] = useState<City[]>([]);
  const [isApplyEnabled, setIsApplyEnabled] = useState(false);

  const resetSelections = () => {
    setSelectedRegion('');
    setSelectedCountry(null);
    setSelectedCities([]);
    setIsApplyEnabled(false);
  };

  const handleClose = () => {
    resetSelections();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      resetSelections();
    }
  }, [open]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const CompanyNo = localStorage.getItem('CompanyNo');
        if (!CompanyNo) return;

        const postData = [
          { "컬럼명": "업체번호", "값": CompanyNo }
        ];

        const response = await fetchApi<ApiResponse>("ITN_S_업체국가도시", postData);
        if (response.data?.table) {
          setSearchData(response.data.table);

          // Extract unique regions from 코드명
          const uniqueRegions = Array.from(
            new Set(response.data.table.map(item => item.코드명))
          ).filter(Boolean).sort();
          setRegions(uniqueRegions);

          // Extract unique countries
          const uniqueCountries = Array.from(
            new Set(response.data.table.map(item => item.국가번호))
          ).map(code => {
            const country = response.data.table.find(item => item.국가번호 === code);
            return {
              name: country!.국가한글명,
              code: code
            };
          });
          setCountries(uniqueCountries);

          // Extract unique cities
          const uniqueCities = Array.from(
            new Set(response.data.table.map(item => item.도시번호))
          ).map(code => {
            const city = response.data.table.find(item => item.도시번호 === code);
            return {
              name: city!.도시한글명,
              code: code.toString()
            };
          });
          setCities(uniqueCities);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    if (open) {
      fetchInitialData();
    }
  }, [open]);

  const handleRegionSelect = (region: string) => {
    if (selectedRegion === region) {
      setSelectedRegion('');
      setSelectedCountry(null);
      setSelectedCities([]);
      setIsApplyEnabled(false);

      // Reset to show all options
      const uniqueCountries = Array.from(
        new Set(searchData.map(item => item.국가번호))
      ).map(code => {
        const country = searchData.find(item => item.국가번호 === code);
        return {
          name: country!.국가한글명,
          code: code
        };
      });
      setCountries(uniqueCountries);

      const uniqueCities = Array.from(
        new Set(searchData.map(item => item.도시번호))
      ).map(code => {
        const city = searchData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(uniqueCities);
      return;
    }

    setSelectedRegion(region);
    setSelectedCountry(null);
    setSelectedCities([]);
    setIsApplyEnabled(false);

    // Filter compatible items
    const compatibleData = searchData.filter(item => item.코드명 === region);

    const filteredCountries = Array.from(
      new Set(compatibleData.map(item => item.국가번호))
    ).map(code => {
      const country = compatibleData.find(item => item.국가번호 === code);
      return {
        name: country!.국가한글명,
        code: code
      };
    });

    setCountries(filteredCountries);

    const filteredCities = Array.from(
      new Set(compatibleData.map(item => item.도시번호))
    ).map(code => {
      const city = compatibleData.find(item => item.도시번호 === code);
      return {
        name: city!.도시한글명,
        code: code.toString()
      };
    });
    setCities(filteredCities);
  };

  const handleCountrySelect = (country: Country | null) => {
    setSelectedCountry(country);
    setSelectedCities([]);
    setIsApplyEnabled(!!country);

    if (country) {
      const compatibleData = searchData.filter(item =>
        item.국가번호 === country.code &&
        (selectedRegion ? item.코드명 === selectedRegion : true)
      );

      const filteredCities = Array.from(
        new Set(compatibleData.map(item => item.도시번호))
      ).map(code => {
        const city = compatibleData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(filteredCities);
    } else {
      // Reset to show all compatible options
      const compatibleData = selectedRegion
        ? searchData.filter(item => item.코드명 === selectedRegion)
        : searchData;

      const filteredCities = Array.from(
        new Set(compatibleData.map(item => item.도시번호))
      ).map(code => {
        const city = compatibleData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(filteredCities);
    }
  };

  const handleCitySelect = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
    setIsApplyEnabled(!!city);
  };

  const handleApply = () => {
    let searchValue: string | undefined;
    
    if (selectedCities.length > 0) {
      // 도시가 선택된 경우 도시명만 사용
      searchValue = selectedCities[0].name;
    } else if (selectedCountry) {
      // 국가만 선택된 경우 국가명 사용
      searchValue = selectedCountry.name;
    }

    handleClose();
    if (searchValue) {
      onSelect(undefined, searchValue);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={isMobile}
      fullScreen={isMobile}
      transitionDuration={0}
      PaperProps={{
        sx: {
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? 0 : '62px',
          left: isMobile ? 0 : (anchorEl ? `${anchorEl.getBoundingClientRect().left}px` : '50%'),
          transform: isMobile ? 'none' : (anchorEl ? 'none' : 'translateX(-50%)'),
          width: isMobile ? '100%' : width,
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100vh' : 'calc(100vh - 80px)',
          minHeight: isMobile ? '100vh' : '820px',
          borderRadius: isMobile ? 0 : '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          overflow: 'visible',
          margin: 0,
        }
      }}
    >
      <DialogContent sx={{ p: 2, height: '100%' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          height: '100%',
          pt: isMobile ? 2 : 0
        }}>
          {/* 지역 선택 */}
          <Box sx={{ flex: '0 0 auto' }}>
            <Typography variant="subtitle2" sx={{
              mb: 0.5,
              fontWeight: 600,
              color: 'text.secondary',
            }}>
              지역
            </Typography>
            <Box sx={{
              p: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              maxHeight: isMobile ? '25vh' : '90px',
              overflowY: 'auto'
            }}>
              <Grid container spacing={0.5}>
                {regions.map((region) => (
                  <Grid item xs={4} key={region}>
                    <Button
                      variant={selectedRegion === region ? "contained" : "text"}
                      onClick={() => handleRegionSelect(region)}
                      size="small"
                      sx={{
                        width: '100%',
                        fontSize: '12px',
                        height: '28px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: selectedRegion === region ? 'white' : 'inherit',
                        '&:hover': {
                          backgroundColor: selectedRegion === region ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      {region || '지역 없음'}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* 국가 선택 */}
          <Box sx={{ flex: '0 0 auto' }}>
            <Typography variant="subtitle2" sx={{
              mb: 0.5,
              fontWeight: 600,
              color: 'text.secondary'
            }}>
              국가
            </Typography>
            <Box sx={{
              p: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              maxHeight: isMobile ? '25vh' : '250px',
              overflowY: 'auto'
            }}>
              <Grid container spacing={0.5}>
                {countries.map((country) => (
                  <Grid item xs={4} key={country.code}>
                    <Button
                      variant={selectedCountry?.code === country.code ? "contained" : "text"}
                      onClick={() => handleCountrySelect(selectedCountry?.code === country.code ? null : country)}
                      size="small"
                      sx={{
                        width: '100%',
                        fontSize: '12px',
                        height: '28px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: selectedCountry?.code === country.code ? 'white' : 'inherit',
                        '&:hover': {
                          backgroundColor: selectedCountry?.code === country.code ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      {country.name || '국가 없음'}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* 도시 선택 */}
          <Box sx={{ flex: '1 1 auto' }}>
            <Typography variant="subtitle2" sx={{
              mb: 0.5,
              fontWeight: 600,
              color: 'text.secondary'
            }}>
              도시
            </Typography>
            <Box sx={{
              p: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              height: isMobile ? '25vh' : 'auto',
              maxHeight: isMobile ? '25vh' : 'calc(100vh - 600px)',
              overflowY: 'auto'
            }}>
              <Grid container spacing={0.5}>
                {cities.map((city) => (
                  <Grid item xs={4} key={city.code}>
                    <Button
                      variant={selectedCities.some(c => c.code === city.code) ? "contained" : "text"}
                      onClick={() => handleCitySelect(selectedCities.some(c => c.code === city.code) ? null : city)}
                      size="small"
                      sx={{
                        width: '100%',
                        fontSize: '12px',
                        height: '28px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: selectedCities.some(c => c.code === city.code) ? 'white' : 'inherit',
                        '&:hover': {
                          backgroundColor: selectedCities.some(c => c.code === city.code) ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      {city.name || '도시 없음'}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* 적용 버튼 */}
          <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!isApplyEnabled}
              sx={{
                height: '36px',
                minWidth: '80px',
                bgcolor: '#2D2D2D',
                '&:hover': {
                  bgcolor: '#1a1a1a',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              적용
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CountryCitySearchDialog; 